import { FC, ReactNode, useEffect, useState } from 'react';

import { Button } from '@/components/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/ui/dialog';
import { ResultUtxo } from '@/networks/btc-like/common/transactions/api';
import { BtcLikeCurrency } from '@/types';

import UtxoSelectGrid from './UtxoSelectGrid';

const QuickSelectionButton = ({ title, onClick }: { title: string; onClick: () => void }) => {
  return (
    <Button
      onClick={onClick}
      size="sm"
      variant="secondary"
      className="font-roboto hover:bg-foreground/80 bg-foreground h-auto p-2 text-xs font-medium"
    >
      {title}
    </Button>
  );
};

export interface UtxoSelectModalProps {
  description?: string | ReactNode;
  disabled?: boolean;
  infoComponent?: ReactNode;
  isOpen?: boolean;
  isShowFiatValue?: boolean;
  onAcceptSelection: (selectedUtxos: ResultUtxo[]) => Promise<void> | void;
  onCancelSelection?: () => void;
  onSelectionChange?: (selectedUtxos: ResultUtxo[]) => void;
  setIsOpen?: (isOpen: boolean) => void;
  showConfirmedOnly?: boolean;
  title?: string | ReactNode;
  confirmedUtxos: ResultUtxo[];
  unconfirmedUtxos: ResultUtxo[];
  selectedUtxos: ResultUtxo[];
  toggleUtxo: (utxo: ResultUtxo) => void;
  resetUtxos: (utxos?: ResultUtxo[]) => void;
  currency: BtcLikeCurrency;
}

const UtxoSelectModal: FC<UtxoSelectModalProps> = ({
  description,
  disabled = false,
  infoComponent,
  isOpen,
  isShowFiatValue = false,
  onAcceptSelection,
  onCancelSelection,
  onSelectionChange,
  setIsOpen,
  showConfirmedOnly = false,
  title = 'Select your preferred UTXOs',
  currency,
  confirmedUtxos,
  unconfirmedUtxos,
  selectedUtxos,
  toggleUtxo,
  resetUtxos,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(isOpen);

  const utxos = showConfirmedOnly ? confirmedUtxos : [...confirmedUtxos, ...unconfirmedUtxos];

  const handleCancel = () => {
    setIsOpen?.(false);
    setIsModalOpen(false);
    onCancelSelection?.();
  };

  const handleAccept = async () => {
    await Promise.resolve(onAcceptSelection?.(selectedUtxos)).then(() => {
      setIsOpen?.(false);
      setIsModalOpen(false);
    });
  };

  useEffect(() => {
    setIsModalOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    onSelectionChange?.(selectedUtxos);
  }, [selectedUtxos]);

  if (!utxos?.length) {
    return null;
  }

  return (
    <Dialog open={isModalOpen} onOpenChange={handleCancel}>
      <DialogContent className="z-50 flex max-h-[90svh] max-w-3xl flex-col gap-6 overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : (
            <DialogDescription>
              You have {utxos.length} unspent transaction output(s) available: {confirmedUtxos.length} confirmed,{' '}
              {unconfirmedUtxos.length} unconfirmed.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="max-h-[68svh] overflow-y-scroll" style={{ scrollbarWidth: 'none' }}>
          <div className="flex flex-col gap-6">
            {infoComponent}

            <div className="flex flex-wrap justify-start gap-1">
              <QuickSelectionButton title="Select all" onClick={() => resetUtxos(utxos)} />
              <QuickSelectionButton title="Select all confirmed" onClick={() => resetUtxos(confirmedUtxos)} />
              <QuickSelectionButton title="Select all unconfirmed" onClick={() => resetUtxos(unconfirmedUtxos)} />
              <QuickSelectionButton title="Reset selection" onClick={() => resetUtxos()} />
            </div>

            <UtxoSelectGrid
              currency={currency}
              utxos={utxos}
              isShowFiatValue={isShowFiatValue}
              selectedUtxos={selectedUtxos}
              onUtxoSelect={toggleUtxo}
            />
          </div>
        </div>

        <DialogFooter className="z-10 mt-auto">
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button disabled={disabled} onClick={handleAccept}>
            Proceed
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UtxoSelectModal;
